import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { fmtL, fmtNum, fmtDate, apiErr } from "@/lib/format";
import { toast } from "sonner";
import { WarningOctagonIcon, ClockIcon, ShieldWarningIcon, WrenchIcon, FlowerIcon } from "@phosphor-icons/react";

const RANGOS = [
  { v: 7, l: "7 días", color: "text-red-400" },
  { v: 30, l: "30 días", color: "text-agro-yellow" },
  { v: 60, l: "60 días", color: "text-text-primary" },
  { v: 90, l: "90 días", color: "text-text-secondary" },
];

const estadoCfg = (estado) => {
  const map = {
    vencido: { l: "Vencido", color: "text-red-400 border-red-400/50 bg-red-500/10", icon: WarningOctagonIcon },
    critico: { l: "Crítico (≤7d)", color: "text-red-400 border-red-400/40 bg-red-500/10", icon: WarningOctagonIcon },
    proximo: { l: "Próximo (≤30d)", color: "text-agro-yellow border-agro-yellow/40 bg-agro-yellow/10", icon: ClockIcon },
    advertencia: { l: "Advertencia", color: "text-text-secondary border-border bg-app", icon: ShieldWarningIcon },
  };
  return map[estado] || map.advertencia;
};

export default function ExpirationAlerts() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);

  const load = async (d = days) => {
    setLoading(true);
    try {
      const { data } = await api.get("/inventory/expiration-alerts", { params: { days: d } });
      setItems(data);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(days); }, [days]);

  // Pre-rellenar modal de ajuste con motivo="vencimiento" y cantidad = saldo del lote
  const ajustarVencimiento = (it) => {
    // Navegamos a Ajustes con un querystring que la página puede leer (más simple: localStorage)
    try {
      localStorage.setItem("prefilledAdjustment", JSON.stringify({
        producto_id: it.producto_id, producto_codigo: it.producto_codigo,
        cantidad: it.cantidad_restante, motivo: "vencimiento",
        entry_id: it.entry_id, bodega_id: it.bodega_id || "",
        descripcion: `Vencimiento lote ${it.lote || "—"} (entry ${it.entry_id}) — ${it.fecha_vencimiento}`,
      }));
      toast.info("Abriendo ajuste pre-llenado con motivo Vencimiento");
      navigate("/inventario/ajustes?prefill=1");
    } catch { /* tolerable */ }
  };

  const totales = items.reduce((acc, x) => {
    acc.valor += x.valor_en_riesgo || 0;
    if (x.estado === "vencido" || x.estado === "critico") acc.criticos++;
    if (x.estado === "proximo") acc.proximos++;
    return acc;
  }, { valor: 0, criticos: 0, proximos: 0 });

  return (
    <Layout
      title="Alertas de vencimiento"
      subtitle={`Lotes (y semillas por fecha de test) con vencimiento próximo · ${items.length} lotes monitoreados`}
      actions={
        <div className="flex gap-2">
          {RANGOS.map((r) => (
            <button key={r.v} onClick={() => setDays(r.v)}
              data-testid={`range-${r.v}d`}
              className={`px-3 py-2 text-xs rounded-md border transition-all ${
                days === r.v
                  ? "border-agro-yellow text-agro-yellow bg-agro-yellow/10 font-medium"
                  : "border-border text-text-secondary hover:text-text-primary"
              }`}>
              {r.l}
            </button>
          ))}
        </div>
      }
    >
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-card border border-red-400/30 rounded-lg p-4" data-testid="kpi-criticos">
          <div className="text-[10px] uppercase tracking-wider text-text-muted mb-1.5 flex items-center gap-1.5">
            <WarningOctagonIcon size={12} className="text-red-400" /> Vencidos / Críticos (≤7d)
          </div>
          <div className="text-2xl font-heading font-semibold text-red-400">{totales.criticos}</div>
        </div>
        <div className="bg-card border border-agro-yellow/30 rounded-lg p-4" data-testid="kpi-proximos">
          <div className="text-[10px] uppercase tracking-wider text-text-muted mb-1.5 flex items-center gap-1.5">
            <ClockIcon size={12} className="text-agro-yellow" /> Próximos (≤30d)
          </div>
          <div className="text-2xl font-heading font-semibold text-agro-yellow">{totales.proximos}</div>
        </div>
        <div className="bg-card border border-border/60 rounded-lg p-4" data-testid="kpi-valor-riesgo">
          <div className="text-[10px] uppercase tracking-wider text-text-muted mb-1.5">Valor total en riesgo</div>
          <div className="text-2xl font-heading font-semibold text-text-primary">{fmtL(totales.valor)}</div>
        </div>
      </div>

      <div className="bg-card border border-border/60 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-sidebar text-[10px] uppercase tracking-wider text-text-muted">
              <tr className="text-left">
                <th className="p-3 pl-5">Producto</th>
                <th className="p-3">Bodega</th>
                <th className="p-3">Batch</th>
                <th className="p-3">Vence</th>
                <th className="p-3 text-right">Días</th>
                <th className="p-3 text-right">Saldo</th>
                <th className="p-3 text-right">Valor en riesgo</th>
                <th className="p-3">Estado</th>
                <th className="p-3 pr-5 text-right">Acción</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="9" className="p-8 text-center text-text-muted">Cargando…</td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan="9" className="p-12 text-center text-text-muted">
                  <ShieldWarningIcon size={32} className="inline mb-2 opacity-40" />
                  <div>No hay lotes por vencer en los próximos {days} días</div>
                </td></tr>
              ) : items.map((it) => {
                const cfg = estadoCfg(it.estado);
                const Icon = cfg.icon;
                return (
                  <tr key={it.entry_id} className="border-t border-border/40 hover:bg-white/[0.02]" data-testid={`expiry-row-${it.entry_id}`}>
                    <td className="p-3 pl-5">
                      <div className="flex items-center gap-1.5">
                        <span className="font-mono text-xs text-agro-green">{it.producto_codigo}</span>
                        {it.es_semilla && <FlowerIcon size={12} weight="fill" className="text-agro-yellow" />}
                      </div>
                      <div className="text-[11px] text-text-muted">{it.sku}</div>
                    </td>
                    <td className="p-3 text-xs">
                      <div className="font-mono text-text-primary">{it.bodega_codigo}</div>
                      <div className="text-[10px] text-text-muted">{it.bodega_nombre}</div>
                    </td>
                    <td className="p-3 text-xs font-mono">{it.lote || "—"}</td>
                    <td className="p-3 text-xs">{fmtDate(it.fecha_vencimiento)}{it.base_fecha === "fecha_test" && <div className="text-[10px] text-agro-yellow" title="Semilla: vigencia del test de germinación">test {fmtDate(it.fecha_test)}</div>}</td>
                    <td className={`p-3 text-right text-sm font-medium ${it.dias_para_vencer < 0 ? "text-red-400" : it.dias_para_vencer <= 7 ? "text-red-400" : it.dias_para_vencer <= 30 ? "text-agro-yellow" : "text-text-secondary"}`}>
                      {it.dias_para_vencer < 0 ? `${Math.abs(it.dias_para_vencer)}d vencido` : `${it.dias_para_vencer}d`}
                    </td>
                    <td className="p-3 text-right">{fmtNum(it.cantidad_restante)} {it.unidad}</td>
                    <td className="p-3 text-right font-medium">{fmtL(it.valor_en_riesgo)}</td>
                    <td className="p-3 text-xs">
                      <span className={`px-2 py-0.5 rounded border inline-flex items-center gap-1 ${cfg.color}`}>
                        <Icon size={10} /> {cfg.l}
                      </span>
                    </td>
                    <td className="p-3 pr-5 text-right">
                      <button onClick={() => ajustarVencimiento(it)} data-testid={`adjust-${it.entry_id}`}
                        className="text-xs px-2.5 py-1 border border-border rounded hover:border-agro-yellow/40 text-text-secondary hover:text-agro-yellow flex items-center gap-1 ml-auto"
                        title="Crear ajuste de Vencimiento pre-llenado">
                        <WrenchIcon size={11} /> Ajustar
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </Layout>
  );
}
